import { Icon } from '@chakra-ui/icons'
import { HStack, Text } from '@chakra-ui/layout'
import { Badge, Box, Tooltip, VStack, Center, Flex } from '@chakra-ui/react'
import { Base64 } from 'js-base64'
import type { ReactElement } from 'react'
import React from 'react'
import { MdOutlineCollections } from 'react-icons/md'
import { Link, useHistory } from 'react-router-dom'
import type { MediaLibrarySearchCollectionResponse } from '../../../../interfaces'
import {
  MediaLibraryActions,
  MediaLibraryActionType,
} from '../../../../providers/MLProvider'
import { ContextMenuTrigger } from '../../ContextMenu'
import Hover from '../../../Hover'
import { useGetQueryParams } from '../../../../hooks/useGetQueryParams'
import EllipsisMiddle from '../../../EllipsisMiddle'
import { colors } from '../../../../../ui-kit'

interface MediaLibrarySearchCollectionResponseWithSelection
  extends MediaLibrarySearchCollectionResponse {
  onSelect(idx: number, shiftKeyPressed: boolean): void
  idx: number
  isMediaCollectionSelected: boolean
  dispatch: React.Dispatch<MediaLibraryActionType | MediaLibraryActionType[]>
}

const MediaCollectionElement = (
  mediaCollection: MediaLibrarySearchCollectionResponseWithSelection,
): ReactElement => {
  const {
    name,
    mediaCollectionId,
    size,
    onSelect,
    idx,
    isMediaCollectionSelected,
    dispatch,
  } = mediaCollection

  const history = useHistory()

  const queryFromUrl = useGetQueryParams()

  const handleClick: React.MouseEventHandler<HTMLDivElement> = e => {
    e.preventDefault()
    if (onSelect) {
      onSelect(idx, e.shiftKey ? true : false)
    }
    dispatch([
      {
        type: MediaLibraryActions.SELECT_MEDIA_COLLECTION,
        payload: {
          mediaCollection,
          event: e,
        },
      },
      {
        type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
      },
    ])
  }

  const handleDoubleClick: React.MouseEventHandler<HTMLDivElement> = () => {
    dispatch([
      {
        type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
      },
      {
        type: MediaLibraryActions.RESET_MEDIA_COLLECTION_SEARCH,
      },
    ])
    history.push(
      `/media-collections/${mediaCollectionId}/${queryFromUrl as string}`,
    )
  }

  return (
    <ContextMenuTrigger
      onTrigger={event => {
        if (!isMediaCollectionSelected) {
          dispatch({
            type: MediaLibraryActions.SELECT_MEDIA_COLLECTION,
            payload: {
              mediaCollection,
              event,
            },
          })
        }
      }}
      dispatch={dispatch}
    >
      {contextMenuProps => (
        <VStack
          borderColor={
            isMediaCollectionSelected ? 'appClearBlue' : 'appPaleLilac'
          }
          borderWidth="1px"
          cursor="pointer"
          onClick={handleClick}
          spacing={0}
          onDoubleClick={handleDoubleClick}
          overflow="hidden"
          as={Link}
          to={`/media-collections/${mediaCollectionId}/`}
          justifyContent="flex-start"
          alignItems="flex-start"
          borderRadius="lg"
          {...contextMenuProps}
        >
          <Box
            width="100%"
            height="100%"
            position="relative"
            borderBottomWidth="1px"
          >
            <Center margin="24px" alignContent="center">
              <Icon
                as={MdOutlineCollections}
                boxSize={36}
                color="appDarkIndigo"
                position="relative"
              />
            </Center>
          </Box>
          <Hover w="full">
            {isHover => (
              <Tooltip label={name} placement="top-start" zIndex={9}>
                <VStack
                  spacing={1}
                  paddingX={2}
                  paddingY={1}
                  width="100%"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                >
                  <Flex
                    width="100%"
                    color="appDarkIndigo"
                    fontWeight="semibold"
                    fontSize="md"
                  >
                    <Text
                      className="asset-name"
                      noOfLines={1}
                      width="100%"
                      color={colors.appDarkIndigo}
                    >
                      {name}
                    </Text>
                  </Flex>
                  <Text color="appSlate" fontSize="sm" isTruncated width="100%">
                    {size === 0
                      ? 'no items'
                      : size === 1
                      ? `${size} item`
                      : `${size} items`}
                  </Text>
                </VStack>
              </Tooltip>
            )}
          </Hover>
        </VStack>
      )}
    </ContextMenuTrigger>
  )
}

export default React.memo(MediaCollectionElement)
